import {type Subset} from '@wandb/weave/common/types/base';
import gql from 'graphql-tag';

import * as Generated from '../../generated/graphql';
import {
  type Entity,
  type Graph,
  type ProjectField,
  type Tag,
  type TagCount,
} from '../../types/graphql';
import * as Filter from '../../util/filters';
import type * as FilterTypes from '../../util/filterTypes';
import {emptyQueryFilter} from '../../util/filterV2';
import {addSearchFilter} from '../../util/queryts';
import type * as RunTypes from '../../util/runTypes';
import {type RunSetConfig} from '../../util/section';
import * as SM from '../../util/selectionmanager';
import {ARTIFACT_COUNTS_FRAGMENT} from '../runs-low/graphql';
import {type CustomRunNames} from '../views/customRunNames/types';
import {type ApolloQueryResult} from './apolloWithAbort';
import {useQuery} from './query';
///// Query definition

export const RUN_SELECTOR_QUERY = gql`
  query RunSelector($projectInternalId: ID!) {
    project(internalId: $projectInternalId) {
      id
      fields(
        types: ["number", "string", "boolean", "other"]
        columns: [
          "config"
          "summary_metrics"
          "system_metrics"
          "aggregations_min"
          "aggregations_max"
        ]
        first: 2000
      ) {
        edges {
          node {
            path
          }
        }
      }
      entity {
        id
        settings {
          storageBucketInfo {
            ID
          }
        }
      }
    }
  }
`;

export const RUN_FIELDS_FRAGMENT = gql`
  fragment RunFields on Run {
    id
    agent {
      id
      name
    }
    aggregations(keys: $aggregationKeys) @include(if: $enableAggregations)
    commit
    computeSeconds
    config(keys: $configKeys) @include(if: $enableConfig)
    createdAt
    defaultColorIndex
    displayName
    framework
    github
    group
    groupCounts
    heartbeatAt
    host
    jobType
    logLineCount
    multiValueMetadataColumns(
      keys: $groupedMetadataColumnKeys
      limit: $groupedMetadataColumnLimit
    ) @include(if: $enableGroupedMetadataColumns) {
      key
      values
    }
    name
    notes
    projectId
    readOnly
    runInfo {
      gpu
      gpuCount
    }
    shouldStop
    state
    stopped
    summaryMetrics(keys: $summaryKeys) @include(if: $enableSummary)
    sweep {
      id
      name
      displayName
      config
    }
    tags: tagColors @include(if: $enableTags) {
      id
      name
      colorIndex
    }
    updatedAt
    user {
      id
      username
      photoUrl
    }
    wandbConfig(keys: $wandbKeys)
    ...ArtifactCountsFragment @include(if: $enableArtifactCounts)
  }
`;

export const PROJECT_FRAGMENT = gql`
  fragment RunsSelectorRunsProjectFragment on Project {
    id
    runs(
      first: $limit
      after: $after
      order: $order
      filters: $filters
      groupKeys: $groupKeys
      groupAggregation: $groupAggregation
      groupLevel: $groupLevel
    ) {
      totalCount
      deltaOrder(currentRuns: $currentRuns, lastUpdated: $lastUpdated)
      delta(currentRuns: $currentRuns, lastUpdated: $lastUpdated) {
        index
        op
        run {
          ...RunFields
        }
      }
    }
  }
`;

export const RUN_SELECTOR_GROUPS_LEAN = gql`
  query RunSelectorGroupsLean(
    $after: String
    $filters: JSONString!
    $groupKeys: [String!]!
    $groupLevel: Int!
    $limit: Int!
    $order: String!
    $projectInternalId: ID!
  ) {
    project(internalId: $projectInternalId) {
      id
      runSelectorGroups(
        first: $limit
        after: $after
        order: $order
        filters: $filters
        groupKeys: $groupKeys
        groupLevel: $groupLevel
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            value
            label
            count
            subgroupCount
            hasActiveRuns
          }
        }
      }
    }
  }
`;

/**
 * Query to retrieve runs data for the RunSelector.
 * We use deltas for this request because it's more
 * performant than plain runs especially for polling.
 */
export const RUN_SELECTOR_RUNS = gql`
  query RunSelectorRuns(
    $after: String
    $aggregationKeys: [String!]
    $configKeys: [String!]
    $currentRuns: [String!]!
    $enableAggregations: Boolean = false
    $enableArtifactCounts: Boolean = false
    $enableConfig: Boolean = false
    $enableGroupedMetadataColumns: Boolean = false
    $enableSummary: Boolean = false
    $enableTags: Boolean = false
    $filters: JSONString!
    $groupAggregation: GroupAggregation
    $groupedMetadataColumnKeys: [String!]!
    $groupedMetadataColumnLimit: Int
    $groupKeys: [String!]!
    $groupLevel: Int!
    $lastUpdated: DateTime!
    $limit: Int!
    $order: String!
    $pinnedRunIds: [ID!]!
    $projectInternalId: ID!
    $summaryKeys: [String!]
    $wandbKeys: [String!]
  ) {
    pinnedRuns: runs(ids: $pinnedRunIds, order: $order) {
      runs {
        project {
          id
          name
          entityName
        }
        ...RunFields
      }
    }
    project(internalId: $projectInternalId) {
      ...RunsSelectorRunsProjectFragment
    }
  }
  ${PROJECT_FRAGMENT}
  ${RUN_FIELDS_FRAGMENT}
`;

export const RUN_SELECTOR_FILTERED_RUN_EXISTS = gql`
  query RunSelectorFilteredRunExists(
    $projectInternalId: ID!
    $filters: JSONString!
  ) {
    project(internalId: $projectInternalId) {
      id
      runs(first: 1, filters: $filters) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;

/** Run fields for CSV export query */
export const RUN_SELECTOR_CSV_EXPORT_RUN_FIELDS_FRAGMENT = gql`
  fragment RunSelectorCsvExportRunFields on Run {
    id
    aggregations(keys: $aggregationKeys) @include(if: $enableAggregations)
    commit
    config(keys: $configKeys) @include(if: $enableConfig)
    createdAt
    displayName
    framework
    github
    group
    groupCounts
    heartbeatAt
    host
    jobType
    logLineCount
    multiValueMetadataColumns(
      keys: $groupedMetadataColumnKeys
      limit: $groupedMetadataColumnLimit
    ) @include(if: $enableGroupedMetadataColumns) {
      key
      values
    }
    name
    notes
    projectId
    readOnly
    runInfo {
      gpu
      gpuCount
    }
    shouldStop
    state
    stopped
    summaryMetrics(keys: $summaryKeys) @include(if: $enableSummary)
    sweep {
      id
      name
      displayName
    }
    tags @include(if: $enableTags)
    updatedAt
    user {
      id
      username
    }
    wandbConfig(keys: $wandbKeys)
    ...ArtifactCountsFragment @include(if: $enableArtifactCounts)
  }
  ${ARTIFACT_COUNTS_FRAGMENT}
`;

/** Query to retrieve data for RunSelector CSV export */
export const RUN_SELECTOR_CSV_EXPORT_RUNS = gql`
  query RunSelectorCsvExportRuns(
    $aggregationKeys: [String!]
    $configKeys: [String!]
    $enableAggregations: Boolean = false
    $enableArtifactCounts: Boolean = false
    $enableConfig: Boolean = false
    $enableGroupedMetadataColumns: Boolean = false
    $enableSummary: Boolean = false
    $enableTags: Boolean = false
    $entityName: String!
    $filters: JSONString!
    $groupAggregation: GroupAggregation
    $groupedMetadataColumnKeys: [String!]!
    $groupedMetadataColumnLimit: Int
    $groupKeys: [String!]!
    $groupLevel: Int!
    $limit: Int!
    $order: String!
    $pinnedRunIds: [ID!]!
    $projectName: String!
    $summaryKeys: [String!]
    $wandbKeys: [String!]
  ) {
    pinnedRuns: runs(ids: $pinnedRunIds, order: $order) {
      runs {
        project {
          id
          name
          entityName
        }
        ...RunSelectorCsvExportRunFields
      }
    }
    project(name: $projectName, entityName: $entityName) {
      id
      runs(
        first: $limit
        order: $order
        filters: $filters
        groupKeys: $groupKeys
        groupAggregation: $groupAggregation
        groupLevel: $groupLevel
      ) {
        edges {
          node {
            ...RunSelectorCsvExportRunFields
          }
        }
      }
    }
  }
  ${RUN_SELECTOR_CSV_EXPORT_RUN_FIELDS_FRAGMENT}
`;

/**
 * Query to retrieve data for determing automatic columns in runs table.
 * Includes all aggregation, config, and summary keys.
 */
export const RUN_SELECTOR_AUTO_COLS_RUNS = gql`
  query RunSelectorAutoColsRuns(
    $enableArtifactCounts: Boolean = false
    $entityName: String!
    $filters: JSONString!
    $groupKeys: [String!]!
    $groupLevel: Int!
    $limit: Int!
    $order: String!
    $projectName: String!
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      runs(
        first: $limit
        order: $order
        filters: $filters
        groupKeys: $groupKeys
        groupLevel: $groupLevel
      ) {
        edges {
          node {
            id
            aggregations
            commit
            config
            createdAt
            displayName
            framework
            github
            group
            groupCounts
            heartbeatAt
            host
            jobType
            logLineCount
            name
            notes
            projectId
            readOnly
            runInfo {
              gpu
              gpuCount
            }
            shouldStop
            state
            stopped
            summaryMetrics
            sweep {
              id
              name
              displayName
            }
            updatedAt
            user {
              id
              username
            }
            ...ArtifactCountsFragment @include(if: $enableArtifactCounts)
          }
        }
      }
    }
  }
`;

/**
 * This is here temporarily to remove it from the `RUN_SELECTOR_QUERY`... eventually the BulkTagEditor
 * should manage this using the same approach as the tag selector in the row with an infinite dropdown.
 *
 * It is a bit too much of a change for the initial PR so i'm leaving this comment here and will come back to it
 * for another pass to extract out this query and rework the bulk editor...
 */
export const BULK_EDITOR_PROJECT_TAGS_QUERY = gql`
  query BulkEditorProjectTags(
    $name: String!
    $entityName: String
    $tempFilters: JSONString
  ) {
    project(name: $name, entityName: $entityName) {
      id
      tags {
        id
        name
        colorIndex
      }
      tagCounts(filters: $tempFilters) {
        name
        count
      }
    }
  }
`;

// These types define the inputs and outputs of the above gql query

export interface RunSelectorData {
  id: string;
  runCount: number;
  fields: Graph<Subset<ProjectField, 'path'>>;
  entity: Entity;
}

export interface BulkEditorProjectTagsData {
  id: string;
  tags: Tag[];
  tagCounts: TagCount[];
}

///// HOC definition

// These are the extra props required by the HOC. All are passed through to your
// wrapped component.
interface InputProps {
  entityName?: string;
  projectName?: string;
  internalId?: string;
  mergeFilters?: FilterTypes.Filter<RunTypes.Key>;
  filters: FilterTypes.Filter<RunTypes.Key>;
  groupTempSelection: SM.GroupSelectionFilterState;
}

export function combinedFilter(
  userFilters?: FilterTypes.Filter<RunTypes.Key>,
  groupSelection?: SM.GroupSelectionFilterState,
  spamFilters?: FilterTypes.Filter<RunTypes.Key>,
  mergeFilters?: FilterTypes.Filter<RunTypes.Key>,
  search?: RunSetConfig['search'],
  /** note: if `search` is provided, `customRunNames` is likely needed too! */
  customRunNames?: CustomRunNames
) {
  const empty = emptyQueryFilter();
  let filter = userFilters ?? empty;
  if (SM.isSelectingIndividualRuns(groupSelection)) {
    // The user is hand-selecting runs so there is no reason to
    // pass the full user filter down. The full filter might be
    // significantly more expensive than a handful of run id lookups.
    filter = empty;
  }

  if (search?.query) {
    filter = addSearchFilter(
      filter,
      search,
      groupSelection?.grouping,
      customRunNames
    );
  }
  const allFilters = [filter];
  if (groupSelection) {
    allFilters.push(SM.toRunFilter(groupSelection));
  }
  if (spamFilters) {
    allFilters.push(spamFilters);
  }
  if (mergeFilters) {
    allFilters.push(mergeFilters);
  }
  return Filter.toMongo(Filter.And(allFilters));
}

type RunSelectorQueryResult = {
  initialLoading: boolean;
  loading: boolean;
  project: Generated.Project;
  refetch: (
    variables?: any
  ) => Promise<ApolloQueryResult<Generated.RunSelectorQuery>>;
};

export function useRunSelectorQuery(
  projectInternalId: string | undefined
): RunSelectorQueryResult {
  const {loading, data, refetch} = Generated.useRunSelectorQuery({
    variables: {projectInternalId: projectInternalId ?? ''},
    skip: projectInternalId == null,
  });

  return {
    initialLoading: loading && !data,
    loading,
    project: data?.project as Generated.Project,
    refetch,
  };
}

// TODO: Delete this in favor of using an infinite dropdown like RunTagInfiniteDropdown
export function useBulkEditorProjectTagsQuery(inputProps: InputProps) {
  const variables = {
    entityName: inputProps.entityName,
    name: inputProps.projectName,
    tempFilters: JSON.stringify(
      combinedFilter(
        inputProps.filters,
        inputProps.groupTempSelection,
        undefined,
        inputProps.mergeFilters
      )
    ),
  };
  return useQuery<
    {
      project: BulkEditorProjectTagsData;
    },
    typeof variables
  >(BULK_EDITOR_PROJECT_TAGS_QUERY, {
    variables,
    fetchPolicy: 'no-cache',
  });
}
